import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { addDays } from 'date-fns';
import _ from 'lodash';
import { AlertTriangle, HelpCircle, Info, Paperclip, Plus } from 'lucide-react';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import applicationsResource from 'modules/application-link/domain/resources/applications.resource';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { mapPlayerToAnnounced } from 'modules/teams/domain/data-mappers/announced-teams.mapper';
import { upsertChampionshipApplicationUsers } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  updatingChampionshipTeamApplicationSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { useMultiSelect } from 'shared/hooks';
import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import EmptyButton from 'ud-ui/components/empty-button';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import useModalEditApplication from 'modules/teams/ui/hooks/use-modal-edit-application';
import { useModalCreatePlayer } from 'modules/teams/ui/hooks/use-modal-create-player';
import ChampionshipApplicationFilters, {
  ChampionshipApplicationFiltersOption,
  ChampionshipTeamsFilterValues,
} from '../championship-application-filters/championship-application-filters';
import { BodyBlock, HeadBlock, TeamStatusBadge } from './styles';
import TeamPlayerCard from './team-player-card';
import { theme } from 'styles/theme/theme';
import type { MessageDescriptor } from 'shared/i18n';

const statusItems: ChampionshipApplicationFiltersOption<ApplicationStatus>[] = [
  {
    status: ApplicationStatus.PENDING,
    color: 'black',
  },
  {
    status: ApplicationStatus.ACCEPTED,
    color: '#58B879',
  },
  {
    status: ApplicationStatus.DECLINED,
    color: theme.colors.error,
  },
];

/**
 * Статус команды в шапке — тот же статус, что определяет вкладку списка заявок
 * (`AnnouncedTeamStatus`, `championship-applications.tsx`): и `ANNOUNCED`, и `PENDING`
 * попадают в «Незаявленные», поэтому подписаны одинаково.
 */
const TEAM_STATUS_BADGE: Record<AnnouncedTeamStatus, { label: MessageDescriptor; tone: 'pending' | 'accepted' | 'declined' }> = {
  [AnnouncedTeamStatus.ANNOUNCED]: { label: msg({ message: 'Незаявлена', context: 'team application status' }), tone: 'pending' },
  [AnnouncedTeamStatus.PENDING]: { label: msg({ message: 'Незаявлена', context: 'team application status' }), tone: 'pending' },
  [AnnouncedTeamStatus.ACCEPTED]: { label: msg({ message: 'Заявлена', context: 'team application status' }), tone: 'accepted' },
  [AnnouncedTeamStatus.DECLINED]: { label: msg({ message: 'Отклонена', context: 'team application status' }), tone: 'declined' },
};

/** Открывая состав, сразу показываем вкладку игроков, из-за которых у команды такой статус. */
const INITIAL_FILTER_BY_TEAM_STATUS: Record<AnnouncedTeamStatus, ApplicationStatus> = {
  [AnnouncedTeamStatus.ANNOUNCED]: ApplicationStatus.PENDING,
  [AnnouncedTeamStatus.PENDING]: ApplicationStatus.PENDING,
  [AnnouncedTeamStatus.ACCEPTED]: ApplicationStatus.ACCEPTED,
  [AnnouncedTeamStatus.DECLINED]: ApplicationStatus.DECLINED,
};

type ChampionshipTeamPlayersProps = {
  teamId: number;
  championshipId: number;
};

const ChampionshipTeamPlayers = ({ teamId, championshipId }: ChampionshipTeamPlayersProps) => {
  const { t, i18n } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const { open, Modal } = useModalEditApplication();
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const updatingApplicationId = useSelector(updatingChampionshipTeamApplicationSelector);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const announcedTeam = announcedTeams ? announcedTeams[teamId] : null;

  const [status, selectStatus] = useState<ApplicationStatus>(() =>
    announcedTeam ? INITIAL_FILTER_BY_TEAM_STATUS[announcedTeam.status] : ApplicationStatus.PENDING,
  );
  const [name, setName] = useState<string>('');

  const { isLoading, data: detailTeam } = useGetTeamByIdQuery(teamId, { skip: !teamId });

  const { open: openCreatePlayer, Modal: CreatePlayerModal } = useModalCreatePlayer({
    teamId,
    players: detailTeam?.players ?? [],
  });

  const mergedPlayers = useMemo(() => {
    if (isLoading || !announcedTeam || !detailTeam?.players) {
      return [];
    }

    const notRegisteredApplications = mapPlayerToAnnounced(detailTeam.players, {
      teamId,
      championshipId,
      teamCreatedAt: detailTeam.createdAt,
    });

    return _.uniqBy([...Object.values(announcedTeam.applications || []), ...notRegisteredApplications], 'player.id');
  }, [isLoading, announcedTeam, detailTeam?.players, detailTeam?.createdAt, teamId, championshipId]);

  const playersList = useMemo(() => {
    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .filter((app) => status === app.status)
      .filter((app) => {
        const query = name.toLowerCase();
        const firstName = app.player.firstName.toLowerCase();
        const lastName = app.player.lastName.toLowerCase();

        return firstName.includes(query) || lastName.includes(query);
      });
  }, [mergedPlayers, status, name]);

  const {
    selectedIds: selectedPlayers,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseUp,
    handleSelect,
    setSelectedIds: setSelectedPlayers,
  } = useMultiSelect<PlayerApplication>({
    items: playersList,
    getId: (item: PlayerApplication) => item.player.id,
  });

  const countByStatus = useMemo(() => {
    const defaultValue = {
      [ApplicationStatus.PENDING]: 0,
      [ApplicationStatus.ACCEPTED]: 0,
      [ApplicationStatus.DECLINED]: 0,
    };

    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .reduce<Record<string, number>>((result, item) => {
        const count = result[item.status] || 0;
        return {
          ...result,
          [item.status]: count + 1,
        };
      }, defaultValue);
  }, [mergedPlayers]);

  const handleFilterChange = useCallback((values: ChampionshipTeamsFilterValues<ApplicationStatus>) => {
    setName(values.name);
    selectStatus(values.status);
  }, []);

  const isAllSelected = playersList.length > 0 && playersList.every((app) => selectedPlayers.includes(app.player.id));

  const handleToggleSelectAll = useCallback(() => {
    setSelectedPlayers(isAllSelected ? [] : playersList.map((app) => app.player.id));
  }, [isAllSelected, playersList, setSelectedPlayers]);

  const handlePlayersStatusChange = useCallback(
    async (status: ApplicationStatus.ACCEPTED | ApplicationStatus.DECLINED) => {
      if (!announcedTeam || selectedPlayers.length === 0) return;

      const applications = mergedPlayers.map((app) => ({
        userId: app.player.id,
        name: app.player.firstName,
        surname: app.player.lastName,
        middleName: app.player.middleName,
        birthDate: app.player.birthDate,
        position: app.position,
        number: app.number,
        state: selectedPlayers.includes(app.player.id) ? status : app.status,
      }));

      // Check max allowed players in application before accepting
      if (status === ApplicationStatus.ACCEPTED) {
        const max = settings?.applicationMaxPlayers ?? 28;
        if (typeof max === 'number') {
          const acceptedCount = applications.filter((a) => a.state === ApplicationStatus.ACCEPTED).length;
          if (acceptedCount > max) {
            toast.error(t`Заявляемое кол-во игроков не соответствует заявочной кампании. Сверьтесь с условиями турнира.`);
            return;
          }
        }
      }

      // Считаем до мутации: после неё в сторе уже новые статусы, и «сколько сменилось»
      // восстановить нечем. Учитываем только реальную смену — повторное принятие уже
      // заявленных игроков события не даёт.
      const newlyAccepted =
        status === ApplicationStatus.ACCEPTED
          ? mergedPlayers.filter((app) => selectedPlayers.includes(app.player.id) && app.status !== ApplicationStatus.ACCEPTED).length
          : 0;

      setIsBulkUpdating(true);
      try {
        await dispatch(
          upsertChampionshipApplicationUsers({
            teamId,
            championshipId,
            applications,
          }),
        ).unwrap();

        StepperMainTool.setByPath('stepper:main:applicationTeam', true);
        trackEvent('Team Application Submitted', { championship_id: championshipId, team_id: teamId, flow: 'v1', source: 'bulk' });
        if (newlyAccepted > 0) {
          trackEvent('Player Application Accepted', {
            championship_id: championshipId,
            team_id: teamId,
            flow: 'v1',
            source: 'bulk',
            players_count: newlyAccepted,
          });
        }
        toast.success(t`Игроки успешно обновлены`);
        setSelectedPlayers([]);
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [
      t,
      dispatch,
      teamId,
      championshipId,
      announcedTeam,
      selectedPlayers,
      setSelectedPlayers,
      settings?.applicationMaxPlayers,
      mergedPlayers,
    ],
  );

  const handleCopyLink = useCallback(async () => {
    const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
    if (endDate) {
      const now = new Date();
      if (endDate < now) {
        toast.warning(t`Заявочная кампания закончилась. Чтобы получить ссылку, продлите дату окончания приёма заявок.`);
        return;
      }
    }

    const token = await applicationsResource.getToken(championshipId, teamId);
    const params = new URLSearchParams();
    params.append('token', token.token);
    const link = `${window.location.origin}${Pages.APPLICATION_LINK.INDEX}?${params.toString()}`;
    navigator.clipboard.writeText(link);
    const expiresAt = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : addDays(new Date(), 1);
    const expiresDate = expiresAt.toLocaleDateString(i18n.locale);
    const expiresTime = expiresAt.toLocaleTimeString(i18n.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    toast.success(t`Ссылка скопирована и будет активна до ${expiresDate} ${expiresTime}`);
  }, [championshipId, settings?.applicationEndDate, teamId, t, i18n.locale]);

  const body = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex-center h-32 w-full">
          <ClipLoader />
        </div>
      );
    }

    if (playersList.length === 0) {
      return (
        <div className="flex-center my-5 h-full">
          <UDText type="body-text" className="text-surfaceRomanSilver30">
            <Trans>Список пуст</Trans>
          </UDText>
        </div>
      );
    }

    return (
      <>
        {playersList.map((app, index) => (
          <TeamPlayerCard
            key={app.player.id}
            index={index + 1}
            teamId={app.teamId}
            player={app}
            isUpdating={updatingApplicationId === app.id || isBulkUpdating}
            isSelected={selectedPlayers.includes(app.player.id)}
            onSelect={handleSelect}
            onMouseDownCheckbox={(event) => handleMouseDown(app.player.id, event)}
            onMouseEnterCheckbox={() => handleMouseEnter(app.player.id)}
            onMouseLeaveCheckbox={() => handleMouseLeave(app.player.id)}
            onClick={() => open(app, teamId)}
            applicationMaxPlayers={settings?.applicationMaxPlayers ?? 28}
            applications={announcedTeam?.applications}
          />
        ))}
      </>
    );
  }, [
    announcedTeam?.applications,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isBulkUpdating,
    isLoading,
    open,
    playersList,
    selectedPlayers,
    settings?.applicationMaxPlayers,
    teamId,
    updatingApplicationId,
  ]);

  if (!announcedTeam) {
    return null;
  }

  const startDate = settings?.applicationStartDate ? new Date(settings.applicationStartDate) : undefined;
  const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
  const now = new Date();
  const isAfterStartDate = startDate ? startDate < now : true;
  const isBeforeEndDate = endDate ? endDate > now : true;

  const isRangeValid = isAfterStartDate && isBeforeEndDate;
  const startDateLabel = startDate?.toLocaleDateString(i18n.locale);
  const endDateLabel = endDate?.toLocaleDateString(i18n.locale);
  const teamStatusBadge = TEAM_STATUS_BADGE[announcedTeam.status];

  // Одна подсказка на всю область копирования ссылки — вместо двух соседних иконок с двумя
  // разными сообщениями. Пока период приёма заявок не подходит, подсказка объясняет причину
  // и ведёт править даты; иначе — что вообще делает кнопка и до какого момента живёт ссылка.
  const copyLinkTooltip = isRangeValid ? (
    <UDText type="subhead">
      <Trans>
        Ссылка для заявки будет скопирована в буфер обмена. Отправьте её в чат, чтобы игроки могли подать заявку на участие в турнире.
        <br />
        Срок ссылки — до даты окончания приёма заявок в{' '}
        <Link
          className="text-LightBlue"
          to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
          state={{ highlight: { applicationEndDate: true } }}
        >
          заявочной кампании
        </Link>{' '}
        или 24 часа с момента создания ссылки, если дата не указана.
      </Trans>
    </UDText>
  ) : (
    <UDText type="subhead">
      {!isAfterStartDate && (
        <div>
          <Trans>
            <Link
              className="text-LightBlue"
              to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
              state={{ highlight: { applicationStartDate: true } }}
            >
              Заявочная кампания
            </Link>{' '}
            ещё не началась. Дата начала {startDateLabel}. Для копирования ссылки измените дату начала приёма заявок.
          </Trans>
        </div>
      )}
      {!isBeforeEndDate && (
        <div>
          <Trans>
            <Link
              className="text-LightBlue"
              to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
              state={{ highlight: { applicationEndDate: true } }}
            >
              Заявочная кампания
            </Link>{' '}
            закончилась. Дата окончания {endDateLabel}. Для копирования ссылки продлите дату окончания приёма заявок.
          </Trans>
        </div>
      )}
    </UDText>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col px-6 pt-4" onMouseUp={handleMouseUp}>
      {(announcedTeam.status === AnnouncedTeamStatus.PENDING || announcedTeam.status === AnnouncedTeamStatus.ANNOUNCED) && (
        <>
          <UDText type="headline" className="mb-4 shrink-0" style={{ fontSize: 34 }}>
            <Trans>Заявите игроков</Trans>
          </UDText>
          <UDText type="body-text" className="text-Black mb-8 shrink-0" style={{ fontSize: 14 }}>
            <Trans>
              Чтобы заявить команду в турнир, нужно заявить минимум одного игрока или весь необходимый состав. После того как вы
              заявите игрока, команда сменит статус. Важно! Количество заявленных игроков не должно выходить за пределы разрешённого
              количества, которое вы указали в{' '}
              <Link className="text-LightBlue" to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}>
                Параметрах турнира
              </Link>
              .
            </Trans>
          </UDText>
        </>
      )}
      <HeadBlock className="shrink-0" data-tone={teamStatusBadge.tone}>
        <div className="flex items-center justify-between gap-6">
          <div className="flex min-w-0 items-center gap-4">
            <TeamLogo size={56} url={announcedTeam.logo?.url} />
            <div className="flex min-w-0 items-center gap-3">
              <UDText type="subhead" className="bold truncate">
                {announcedTeam.name}
              </UDText>
              <UDTippy interactive content={t`Статус команды определяется статусом игроков в этой команде`}>
                <TeamStatusBadge data-tone={teamStatusBadge.tone}>
                  {t(teamStatusBadge.label)}
                  <HelpCircle size={14} />
                </TeamStatusBadge>
              </UDTippy>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-6">
            <UDTippy interactive content={copyLinkTooltip}>
              {/* Обёртка нужна, чтобы подсказка работала и когда кнопка внутри отключена. */}
              <div className="flex items-center gap-1.5">
                <EmptyButton
                  icon={<Paperclip size={16} />}
                  color={theme.colors.EditorAccentBlue}
                  style={{ gap: 6 }}
                  onClick={handleCopyLink}
                  disabled={!isRangeValid}
                >
                  <Trans>Скопировать ссылку</Trans>
                </EmptyButton>
                {isRangeValid ? (
                  <Info size={14} color={theme.colors.SurfaceRomanSilver30} />
                ) : (
                  <AlertTriangle size={14} color={theme.colors.DarkYellow700} />
                )}
              </div>
            </UDTippy>
            <UDButton variant="primary" className="flex items-center gap-2" onClick={openCreatePlayer}>
              <Plus size={16} />
              <Trans>Новый игрок</Trans>
            </UDButton>
          </div>
        </div>
      </HeadBlock>
      <BodyBlock className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0">
          <ChampionshipApplicationFilters<ApplicationStatus>
            selectedStatus={status}
            name={name}
            options={statusItems}
            inputPlaceholder={t`ФИО игрока`}
            countByStatus={countByStatus}
            onChange={handleFilterChange}
            selectedIds={selectedPlayers}
            onAccept={async () => await handlePlayersStatusChange(ApplicationStatus.ACCEPTED)}
            onDecline={async () => await handlePlayersStatusChange(ApplicationStatus.DECLINED)}
            isAllSelected={isAllSelected}
            onToggleSelectAll={handleToggleSelectAll}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" style={{ marginTop: 10 }}>
          {body}
        </div>
      </BodyBlock>
      <Modal />
      {CreatePlayerModal}
    </div>
  );
};

export default ChampionshipTeamPlayers;
